import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Copy, Download, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export function CodeField({ value, onChange, disabled, testId }) {
  return (
    <div className="flex justify-center">
      <InputOTP maxLength={6} value={value} onChange={onChange} disabled={disabled} autoFocus
        inputMode="numeric" autoComplete="one-time-code" data-testid={testId}>
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-11 text-lg" />)}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
}

export function RememberDevice({ checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testId} />
      Trust this device for 30 days
    </label>
  );
}

// Enter a 6-digit app code, or switch to a backup code.
export function MfaCodeStep({ onSubmit, busy, onCancel }) {
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [remember, setRemember] = useState(false);
  const ready = backup ? code.replace(/[^a-z0-9]/gi, '').length === 8 : code.length === 6;
  const submit = async (e) => {
    e.preventDefault();
    if (ready && (await onSubmit(code, remember)) === false) setCode('');
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-code-step">
      <div>
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Two-factor sign-in</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {backup ? 'Enter one of your backup codes. Each one works once.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>
      {backup ? (
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="xxxx-xxxx" autoFocus
          className="font-mono text-center tracking-widest" data-testid="mfa-backup-input" />
      ) : (
        <CodeField value={code} onChange={setCode} disabled={busy} testId="mfa-code-input" />
      )}
      <RememberDevice checked={remember} onChange={setRemember} testId="mfa-remember-device" />
      <Button type="submit" className="w-full bg-black text-white hover:bg-black/90" disabled={!ready || busy} data-testid="mfa-verify-btn">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          onClick={() => { setBackup(!backup); setCode(''); }} data-testid="mfa-toggle-backup">
          {backup ? 'Use my authenticator app' : 'Lost your phone? Use a backup code'}
        </button>
        <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onCancel} data-testid="mfa-cancel">Back</button>
      </div>
    </form>
  );
}

// First sign-in since 2FA became required: scan, then prove one code.
export function MfaSetupStep({ loadSetup, onSubmit, busy, onCancel }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);

  useEffect(() => { loadSetup().then(setSetup).catch(() => onCancel('Your sign-in has expired. Please enter your password again.')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => { e.preventDefault(); if (code.length === 6) onSubmit(code, remember); };
  const copyKey = () => { navigator.clipboard?.writeText(setup.secret); toast.success('Key copied'); };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-setup-step">
      <div>
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Set up two-factor sign-in</h2>
        <p className="text-sm text-muted-foreground mt-1">
          HAL now needs a second step when you sign in. Scan this with Microsoft Authenticator, Google Authenticator or a similar app, then enter the 6-digit code it shows.
        </p>
      </div>
      {!setup ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <img src={setup.qrCode} alt="QR code for your authenticator app" className="h-44 w-44 rounded-md border bg-white p-1" data-testid="mfa-qr-code" />
          <div className="text-xs text-muted-foreground text-center">
            Can't scan it? Enter this key in the app:
            <button type="button" onClick={copyKey} className="mt-1 flex items-center gap-1.5 mx-auto font-mono text-foreground bg-muted px-2 py-1 rounded" data-testid="mfa-secret-key">
              {setup.secret.match(/.{1,4}/g).join(' ')}<Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      <CodeField value={code} onChange={setCode} disabled={!setup || busy} testId="mfa-setup-code-input" />
      <RememberDevice checked={remember} onChange={setRemember} testId="mfa-setup-remember-device" />
      <Button type="submit" className="w-full bg-black text-white hover:bg-black/90" disabled={code.length !== 6 || busy} data-testid="mfa-setup-verify-btn">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Turn on and sign in
      </Button>
      <button type="button" className="block mx-auto text-xs text-muted-foreground hover:text-foreground" onClick={() => onCancel()} data-testid="mfa-setup-cancel">Back</button>
    </form>
  );
}

export function BackupCodesList({ codes }) {
  const text = codes.join('\n');
  const copy = () => { navigator.clipboard?.writeText(text); toast.success('Backup codes copied'); };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`HAL backup codes\n\n${text}\n`], { type: 'text/plain' }));
    Object.assign(document.createElement('a'), { href: url, download: 'hal-backup-codes.txt' }).click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono text-sm" data-testid="mfa-backup-codes">
        {codes.map((c) => <span key={c} className="text-center">{c}</span>)}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" className="flex-1" onClick={copy} data-testid="mfa-backup-copy"><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
        <Button type="button" variant="outline" size="sm" className="flex-1" onClick={download} data-testid="mfa-backup-download"><Download className="h-3.5 w-3.5 mr-1.5" />Download</Button>
      </div>
    </div>
  );
}

// Shown once, straight after setup, before the session starts.
export function BackupCodesStep({ codes, onDone }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="space-y-5" data-testid="mfa-backup-codes-step">
      <div>
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><KeyRound className="h-5 w-5" />Save your backup codes</h2>
        <p className="text-sm text-muted-foreground mt-1">If you lose your phone, each of these codes signs you in once. Keep them somewhere safe. You won't see them again.</p>
      </div>
      <BackupCodesList codes={codes} />
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <Checkbox checked={saved} onCheckedChange={(v) => setSaved(!!v)} data-testid="mfa-backup-saved" />
        I've saved these codes
      </label>
      <Button className="w-full bg-black text-white hover:bg-black/90" disabled={!saved} onClick={onDone} data-testid="mfa-backup-continue">Continue to HAL</Button>
    </div>
  );
}
